import { toDate } from "../../../core/lib/format";

// Semaine d'atelier (lundi → dimanche), bornes de période et formats fr-CA.
// Tout est calculé en heure locale du navigateur : l'atelier et ses employés
// vivent dans le même fuseau que la machine qui affiche l'agenda.

export const DAY_MS = 86_400_000;

export type Timestamp = number | string | null | undefined;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** Lundi 0 h 00 de la semaine qui contient `d`. */
export function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  // getDay() : 0 = dimanche ; on ramène au lundi précédent.
  const offset = (day.getDay() + 6) % 7;
  return addDays(day, -offset);
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(d: Date): boolean {
  return sameDay(d, new Date());
}

/** Clé de jour « AAAA-MM-JJ » en heure locale (même forme que `AtelierDayStats.date`). */
export function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Inverse de `dayKey` ; null si la chaîne n'est pas une date. */
export function parseDayKey(key: string | null | undefined): Date | null {
  if (!key) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Les sept jours de la semaine de `d`, lundi en tête. */
export function weekDays(d: Date): Date[] {
  const monday = startOfWeek(d);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** Grille du mois : de 5 à 6 semaines complètes (lundi → dimanche) autour du mois de `d`. */
export function monthGrid(d: Date): Date[] {
  const first = startOfMonth(d);
  const start = startOfWeek(first);
  // Dimanche de la dernière semaine, calculé en jours civils (pas en
  // millisecondes) : un changement d'heure ne doit pas rogner la grille.
  const last = addDays(startOfWeek(addDays(addMonths(first, 1), -1)), 6);
  const days: Date[] = [];
  for (let cur = start; cur.getTime() <= last.getTime(); cur = addDays(cur, 1)) days.push(cur);
  return days;
}

// ---- Formats ----

const fr = "fr-CA";

/** « 8 h 00 », « 13 h 30 » — l'écriture québécoise des heures. */
export function formatHour(value: Timestamp): string {
  const d = toDate(value);
  if (!d) return "—";
  return `${d.getHours()} h ${String(d.getMinutes()).padStart(2, "0")}`;
}

export function formatTimeRange(start: Timestamp, end: Timestamp): string {
  return `${formatHour(start)} – ${formatHour(end)}`;
}

/** « lundi 2 septembre 2026 ». */
export function formatDayLong(d: Date): string {
  return d.toLocaleDateString(fr, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** « lun. » (en-tête de colonne). */
export function formatWeekday(d: Date): string {
  return d.toLocaleDateString(fr, { weekday: "short" });
}

/** « 2 sept. ». */
export function formatDayShort(value: Timestamp): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(fr, { day: "numeric", month: "short" }) : "—";
}

/** « 2 sept. 2026, 8 h 00 ». */
export function formatDateHour(value: Timestamp): string {
  const d = toDate(value);
  if (!d) return "—";
  return `${d.toLocaleDateString(fr, { day: "numeric", month: "short", year: "numeric" })}, ${formatHour(d.getTime())}`;
}

/** « septembre 2026 ». */
export function formatMonth(d: Date): string {
  return d.toLocaleDateString(fr, { month: "long", year: "numeric" });
}

/** « 31 août – 6 sept. 2026 ». */
export function formatWeekRange(d: Date): string {
  const monday = startOfWeek(d);
  const sunday = addDays(monday, 6);
  const left = monday.toLocaleDateString(fr, { day: "numeric", month: "short" });
  const right = sunday.toLocaleDateString(fr, { day: "numeric", month: "short", year: "numeric" });
  return `${left} – ${right}`;
}

/** « 2 h 30 », « 45 min ». */
export function formatDuration(min: number | null): string {
  if (min === null) return "Durée à préciser";
  if (min <= 0) return "0 min";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

/** Heures décimales pour un bandeau de totaux : « 12,5 h ». */
export function formatHours(min: number): string {
  const hours = Math.round(min / 6) / 10;
  return `${hours.toLocaleString(fr, { maximumFractionDigits: 1 })} h`;
}

// ---- Champs de saisie ----

/** Valeur d'un <input type="datetime-local"> (heure locale, sans secondes). */
export function toDateTimeLocalValue(value: Timestamp): string {
  const d = toDate(value);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dayKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Inverse : valeur d'un datetime-local → ISO 8601 avec décalage (null si vide). */
export function dateTimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Ajoute `min` minutes à une valeur de datetime-local et rend une valeur de datetime-local. */
export function addMinutesLocal(value: string, min: number): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() + min);
  return toDateTimeLocalValue(d.getTime());
}
